/**
 * Calcula a próxima data de execução de um agendamento com base no tipo de periodicidade
 * 
 * @param {Object} tipoAgendamento - Objeto contendo as informações do tipo de agendamento
 * @param {string} tipoAgendamento.periodicidade - Código da periodicidade (D, S, M, A, V, VD, U)
 * @param {number} tipoAgendamento.dias_antecedencia - Dias de antecedência (para periodicidade V)
 * @param {string} tipoAgendamento.dias_semana - Dias da semana (para periodicidade S)
 * @param {number} tipoAgendamento.dia_mes - Dia do mês (para periodicidade M)
 * @param {number} tipoAgendamento.mes_ano - Mês do ano (para periodicidade A)
 * @param {Object} documento - Objeto contendo as informações do documento
 * @param {string|Date} documento.dt_vencimento - Data de vencimento do documento
 * @param {number} [documento.nr_dias_aviso] - Número de dias de aviso configurados no documento (para periodicidade VD)
 * @returns {Object} - Objeto contendo a data e hora calculadas { data, horario, dataEditavel }
 */
export const calcularProximaDataExecucao = (tipoAgendamento, documento) => {
    // Valores padrão
    const dataAtual = new Date();
    const dataHoje = new Date(dataAtual.getFullYear(), dataAtual.getMonth(), dataAtual.getDate());
    let dataCalculada = new Date(dataHoje);
    let horario = tipoAgendamento?.horario ? tipoAgendamento.horario.slice(0, 5) : "08:00";
    let dataEditavel = true;
  
    // Se não há tipo de agendamento, retorna a data atual
    if (!tipoAgendamento) {
      return {
        data: formatarDataISO(dataCalculada),
        horario,
        dataEditavel
      };
    }
  
    // Se o tipo tem horário fixo, usamos ele
    if (tipoAgendamento.horario) {
      horario = tipoAgendamento.horario.slice(0, 5);
      dataEditavel = false;
    }
  
    // Calcular próxima data baseado na periodicidade
    switch (tipoAgendamento.periodicidade) {
      case "D": // Diário
        // Próximo dia útil
        dataCalculada.setDate(dataCalculada.getDate() + 1);
        break;
  
      case "S": // Semanal
        // Buscar próximo dia da semana configurado
        if (tipoAgendamento.dias_semana) {
          const diasSemana = tipoAgendamento.dias_semana.split(',').map(d => parseInt(d.trim()));
          
          // Se tem dias configurados, encontrar o próximo
          if (diasSemana.length > 0) {
            // Dia atual da semana (0=Domingo, 1=Segunda, ..., 6=Sábado)
            const diaAtual = dataAtual.getDay();
            
            // Ordenar dias da semana
            diasSemana.sort((a, b) => a - b);
            
            // Verificar próximo dia da semana
            let proximoDia = diasSemana.find(dia => dia > diaAtual);
            
            if (proximoDia !== undefined) {
              // Tem próximo dia na mesma semana
              const diasParaAdicionar = proximoDia - diaAtual;
              dataCalculada.setDate(dataCalculada.getDate() + diasParaAdicionar);
            } else {
              // Vai para o primeiro dia da próxima semana
              const primeiroDiaSemana = diasSemana[0];
              const diasParaAdicionar = 7 - diaAtual + primeiroDiaSemana;
              dataCalculada.setDate(dataCalculada.getDate() + diasParaAdicionar);
            }
          } else {
            // Se não tem dias configurados, usa 7 dias (semanal genérico)
            dataCalculada.setDate(dataCalculada.getDate() + 7);
          }
        } else {
          // Se não tem dias configurados, usa 7 dias (semanal genérico)
          dataCalculada.setDate(dataCalculada.getDate() + 7);
        }
        break;
  
      case "M": // Mensal
        // Usar o dia do mês configurado ou o dia atual
        const diaMes = tipoAgendamento.dia_mes || dataAtual.getDate();
        
        // Avançar para o próximo mês
        dataCalculada.setMonth(dataCalculada.getMonth() + 1);
        
        // Ajustar para o dia configurado
        const ultimoDiaMes = new Date(dataCalculada.getFullYear(), dataCalculada.getMonth() + 1, 0).getDate();
        dataCalculada.setDate(Math.min(diaMes, ultimoDiaMes));
        break;
  
      case "A": // Anual
        // Usar o mês do ano e dia do mês configurados
        const mesAno = tipoAgendamento.mes_ano || (dataAtual.getMonth() + 1);
        const diaMesAnual = tipoAgendamento.dia_mes || dataAtual.getDate();
        
        // Ajustar para o mês e dia configurados
        dataCalculada.setMonth(mesAno - 1);
        
        // Verificar último dia do mês
        const ultimoDiaMesAnual = new Date(dataCalculada.getFullYear(), mesAno, 0).getDate();
        dataCalculada.setDate(Math.min(diaMesAnual, ultimoDiaMesAnual));
        
        // Se a data calculada for no passado, avançar um ano
        if (dataCalculada < dataHoje) {
          dataCalculada.setFullYear(dataCalculada.getFullYear() + 1);
        }
        break;
  
      case "V": // Dias antes do vencimento
        // Verificar se tem data de vencimento e dias de antecedência
        if (documento?.dt_vencimento && tipoAgendamento.dias_antecedencia) {
          const dataVencimento = new Date(documento.dt_vencimento);
          
          // Verificar se a data de vencimento é válida
          if (!isNaN(dataVencimento.getTime())) {
            // Subtrair os dias de antecedência
            dataCalculada = new Date(dataVencimento);
            dataCalculada.setDate(dataCalculada.getDate() - tipoAgendamento.dias_antecedencia);
            
            // Se a data calculada for no passado, usar a data atual
            if (dataCalculada < dataHoje) {
              dataCalculada = new Date(dataHoje);
            }
          }
        }
        break;
  
      case "VD": // Dias antes do vencimento (Pelo Documento)
        // Verificar se tem data de vencimento e dias de aviso no documento
        if (documento?.dt_vencimento && documento?.nr_dias_aviso) {
          const dataVencimento = new Date(documento.dt_vencimento);
          
          // Verificar se a data de vencimento é válida
          if (!isNaN(dataVencimento.getTime())) {
            // Subtrair os dias de aviso
            dataCalculada = new Date(dataVencimento);
            dataCalculada.setDate(dataCalculada.getDate() - documento.nr_dias_aviso);
            
            // Se a data calculada for no passado, usar a data atual
            if (dataCalculada < dataHoje) {
              dataCalculada = new Date(dataHoje);
            }
          }
        }
        break;
  
      case "U": // Único - usar a data atual para permitir seleção pelo usuário
      default:
        // Manter a data atual
        break;
    }
  
    return {
      data: formatarDataISO(dataCalculada),
      horario,
      dataEditavel,
      dataCompleta: dataCalculada // Adicionando a data completa para fácil formatação
    };
  };
  
  /**
   * Formata a data como YYYY-MM-DD para uso em campos de input
   * 
   * @param {Date} data - Data a ser formatada
   * @returns {string} - Data formatada como YYYY-MM-DD
   */
  export const formatarDataISO = (data) => {
    if (!data || isNaN(data.getTime())) {
      const hoje = new Date();
      return hoje.toISOString().split('T')[0];
    }
  
    const ano = data.getFullYear();
    const mes = String(data.getMonth() + 1).padStart(2, '0');
    const dia = String(data.getDate()).padStart(2, '0');
    
    return `${ano}-${mes}-${dia}`;
  };
  
  /**
   * Formata uma data no formato DD/MM/YYYY
   * 
   * @param {string|Date} dataString - Data a ser formatada (string ou objeto Date)
   * @returns {string} - Data formatada como DD/MM/YYYY ou string vazia se inválida
   */
  export const formatarData = (dataString) => {
    if (!dataString) return ""; // Retorna string vazia se a data não estiver definida
    
    try {
      const data = new Date(dataString);
      // Verificar se a data é válida
      if (isNaN(data.getTime())) {
        return "";
      }
      return data.toLocaleDateString('pt-BR');
    } catch (error) {
      console.error("Erro ao formatar data:", error);
      return "";
    }
  };
  
  /**
   * Formata uma data no formato DD/MM/YYYY HH:MM
   * 
   * @param {Date} data - Data a ser formatada
   * @param {string} horario - Horário no formato HH:MM
   * @returns {string} - Data formatada como DD/MM/YYYY HH:MM
   */
  export const formatarDataHora = (data, horarioPadrao = "08:00") => {
  if (!data) return "";

  try {
    // Extrai data e hora manualmente se data vier como string no formato 'YYYY-MM-DD HH:mm:ss'
    let dataObj;
    let horario = horarioPadrao;

    if (typeof data === "string" && data.includes(" ")) {
      const [dataPart, horaPart] = data.split(" ");
      horario = horaPart?.slice(0, 5) || horarioPadrao;

      const [ano, mes, dia] = dataPart.split("-");
      dataObj = new Date(Number(ano), Number(mes) - 1, Number(dia));
    } else {
      dataObj = new Date(data);
    }

    if (isNaN(dataObj.getTime())) return "";

    const dia = String(dataObj.getDate()).padStart(2, "0");
    const mes = String(dataObj.getMonth() + 1).padStart(2, "0");
    const ano = dataObj.getFullYear();

    return `${dia}/${mes}/${ano} ${horario}`;
  } catch (error) {
    console.error("Erro ao formatar data e hora:", error);
    return "";
  }
};